import { SUBTITLE_FONTS } from '@/lib/constants';
import type { SubtitleFontFamily } from '@/types';
import { logger } from '@/lib/logger';

// フォントのロード状態を追跡
const loadedFonts = new Set<string>();
const loadingFonts = new Map<string, Promise<void>>();

/**
 * Google Fontsからフォントを動的にロードする
 */
export async function loadSubtitleFont(fontFamily: SubtitleFontFamily): Promise<void> {
  // すでにロード済みの場合はスキップ
  if (loadedFonts.has(fontFamily)) {
    return;
  }

  // 現在ロード中の場合は完了を待つ
  const existingPromise = loadingFonts.get(fontFamily);
  if (existingPromise) {
    return existingPromise;
  }

  const fontConfig = SUBTITLE_FONTS.find((f) => f.id === fontFamily);
  if (!fontConfig) {
    logger.warn('Unknown font family requested', { fontFamily });
    return;
  }

  const loadPromise = (async () => {
    try {
      logger.info('Loading subtitle font', { fontFamily, url: fontConfig.url });

      // CSS linkタグを追加してフォントをロード
      const existingLink = document.querySelector(`link[href="${fontConfig.url}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontConfig.url;
        document.head.appendChild(link);

        // CSSファイルのロードを待つ
        await new Promise<void>((resolve, reject) => {
          link.onload = () => resolve();
          link.onerror = () => reject(new Error(`Failed to load font CSS: ${fontFamily}`));
        });
      }

      // フォントの実際のロードを待つ（FontFace API）
      if ('fonts' in document) {
        // フォントが実際に利用可能になるまで待機
        const testString = 'あいうえお漢字ABC123';

        try {
          await document.fonts.load(`400 48px "${fontFamily}"`, testString);
          await document.fonts.load(`700 48px "${fontFamily}"`, testString);
          logger.info('Font loaded successfully', { fontFamily });
        } catch (fontLoadError) {
          // フォントロードに失敗しても続行（フォールバックが使われる）
          logger.warn('Font load via FontFace API failed, continuing anyway', {
            fontFamily,
            error: fontLoadError instanceof Error ? fontLoadError.message : String(fontLoadError)
          });
        }
      }

      loadedFonts.add(fontFamily);
    } catch (error) {
      logger.error('Failed to load subtitle font', error instanceof Error ? error : undefined, { fontFamily });
      throw error;
    } finally {
      loadingFonts.delete(fontFamily);
    }
  })();

  loadingFonts.set(fontFamily, loadPromise);
  return loadPromise;
}

/**
 * 複数のフォントを事前ロードする
 */
export async function preloadAllSubtitleFonts(): Promise<void> {
  const promises = SUBTITLE_FONTS.map((font) =>
    loadSubtitleFont(font.id as SubtitleFontFamily).catch((error) => {
      logger.warn('Failed to preload font', { font: font.id, error });
    })
  );
  await Promise.all(promises);
}

/**
 * フォントがロード済みかどうかを確認
 */
export function isFontLoaded(fontFamily: SubtitleFontFamily): boolean {
  return loadedFonts.has(fontFamily);
}
